// src/components/forms/shared/modal/KennelHeaterManager.tsx

import React, { useState } from 'react';
import { Box, styled, Typography } from '@mui/material';
import { Add as AddIcon, Remove as RemoveIcon } from '@mui/icons-material';
import { StyledTextField, StyledSwitch } from '../FormElements';
import { ModalCancelButton, ModalSaveButton } from '../../../common/Buttons';
import {
  InputContainer,
  IncrementButtonLarge,
  ManagerFooter,
} from '../../../common/modal/ModalElements';

interface KennelHeaterManagerProps {
  kennelSelectionId: number;
  heaterEnabled: boolean;
  heaterNights?: number;
  bookingLength: number;
  onSave: (heaterEnabled: boolean, heaterNights: number) => void;
  onCancel: () => void;
  isLoading?: boolean;
}

const HeaterSwitchContainer = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'flex-start',
  gap: theme.spacing(1.5),
  marginBottom: theme.spacing(1),
}));

const HeaterLabel = styled('label')(({ theme }) => ({
  fontSize: '1.9rem',
  fontWeight: 600,
  color: theme.palette.text.secondary,
  cursor: 'pointer',
  userSelect: 'none',
}));

const HeaterNightsLabel = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'baseline',
  gap: '0.8rem',
  marginBottom: theme.spacing(0.25),
}));

const HeaterNightsText = styled(Typography)(({ theme }) => ({
  fontSize: '1.9rem',
  fontWeight: 600,
  color: theme.palette.text.secondary,
}));

const NightsCounter = styled(Box)(() => ({
  display: 'flex',
  alignItems: 'baseline',
  gap: '0.1rem',
}));

const NightsNumber = styled('span')(({ theme }) => ({
  fontSize: '1.9rem',
  fontWeight: 700,
  color:
    theme.palette.mode === 'dark'
      ? theme.palette.secondary.light
      : theme.palette.secondary.main,
}));

const NightsMax = styled('span', {
  shouldForwardProp: (prop) => prop !== 'isAtMax',
})<{ isAtMax: boolean }>(({ theme, isAtMax }) => ({
  fontSize: '1.9rem',
  fontWeight: 700,
  color: isAtMax
    ? theme.palette.mode === 'dark'
      ? theme.palette.secondary.light
      : theme.palette.secondary.main
    : theme.palette.text.secondary,
}));

const HeaterNightsTextField = styled(StyledTextField)({
  flex: 1,
  minWidth: '120px',
  maxWidth: '120px',
  '& input': {
    textAlign: 'center',
    fontSize: '1.6rem',
    fontWeight: 600,
    padding: '0.5rem',
  },
  '& input:disabled': {
    opacity: 0.5,
    cursor: 'not-allowed',
  },
});

const KennelHeaterManager: React.FC<KennelHeaterManagerProps> = ({
  kennelSelectionId,
  heaterEnabled,
  heaterNights = 0,
  bookingLength,
  onSave,
  onCancel,
  isLoading = false,
}) => {
  const [useHeater, setUseHeater] = useState<boolean>(heaterEnabled);
  const [nights, setNights] = useState<number>(
    heaterEnabled ? heaterNights : 0
  );

  const handleHeaterToggle = (event: React.ChangeEvent<HTMLInputElement>) => {
    const enabled = event.target.checked;
    setUseHeater(enabled);

    if (enabled) {
      if (nights === 0) setNights(bookingLength);
    } else {
      setNights(0);
    }
  };

  const handleNightsIncrement = () => {
    if (nights < bookingLength && useHeater) {
      setNights(nights + 1);
    }
  };

  const handleNightsDecrement = () => {
    if (nights > 0 && useHeater) {
      const newValue = nights - 1;
      setNights(newValue);
      if (newValue === 0) setUseHeater(false);
    }
  };

  const handleNightsChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (!useHeater) return;
    const value = parseInt(e.target.value, 10);
    if (!isNaN(value)) {
      const constrained = Math.max(0, Math.min(value, bookingLength));
      setNights(constrained);
      if (constrained === 0) setUseHeater(false);
    }
  };

  const handleSave = () => {
    onSave(useHeater, nights);
  };

  const canIncrement = nights < bookingLength && useHeater;
  const canDecrement = nights > 0 && useHeater;
  const isAtMax = nights === bookingLength;

  return (
    <Box>
      <Box sx={{ p: 2 }}>
        <HeaterSwitchContainer>
          <StyledSwitch
            id="heater-switch"
            checked={useHeater}
            onChange={handleHeaterToggle}
            disabled={isLoading}
          />
          <HeaterLabel htmlFor="heater-switch">Use Heater</HeaterLabel>
        </HeaterSwitchContainer>

        <HeaterNightsLabel>
          <HeaterNightsText>Nights:</HeaterNightsText>
          <NightsCounter>
            <NightsNumber>{nights}</NightsNumber>
            <NightsMax isAtMax={isAtMax}>/{bookingLength}</NightsMax>
          </NightsCounter>
        </HeaterNightsLabel>

        <InputContainer sx={{ mb: 0 }}>
          <IncrementButtonLarge
            onClick={handleNightsDecrement}
            disabled={!canDecrement || isLoading}
            title="Decrease heater nights"
          >
            <RemoveIcon />
          </IncrementButtonLarge>

          <HeaterNightsTextField
            type="number"
            value={nights}
            onChange={handleNightsChange}
            inputProps={{ min: 0, max: bookingLength, step: 1 }}
            size="small"
            disabled={!useHeater || isLoading}
          />

          <IncrementButtonLarge
            onClick={handleNightsIncrement}
            disabled={!canIncrement || isLoading}
            title="Increase heater nights"
          >
            <AddIcon />
          </IncrementButtonLarge>
        </InputContainer>
      </Box>

      <ManagerFooter sx={{ justifyContent: 'flex-end', mt: 1, pt: 1.5 }}>
        <ModalCancelButton onClick={onCancel} disabled={isLoading} />
        <ModalSaveButton onClick={handleSave} disabled={isLoading} />
      </ManagerFooter>
    </Box>
  );
};

export default KennelHeaterManager;
